/**
 * Starts the OIDC sign-in flow. Popup only in a framed preview (the editor and the like), where the
 * IdP refuses framing and ms-auth's /complete page can message the opener back
 * (PreviewAuthCompleteController is the producer). Everywhere else: full-page redirect, and the
 * promise never resolves because the page leaves.
 */
import { AuthError, me, type AuthUser } from './authApi';

export type OidcProvider = 'google' | 'okta' | 'azuread' | 'auth0';

export interface SignInOptions {
    provider: OidcProvider;
    /**
     * Site-relative path the visitor lands on after a successful sign-in. Pass it: with no
     * `redirect` ms-auth uses a destination cookie an earlier attempt left, else the site root —
     * it never falls back to the Referer. Ignored in a framed preview, which stays on the page.
     */
    redirect?: string;
}

// Must match PreviewAuthCompleteController.MESSAGE_SOURCE — rename both together.
const PREVIEW_AUTH_MESSAGE_SOURCE = 'preview-auth';

// Provider ids are not validated here — RegistrationIdHackersRejectingFilter is the authority.
function authorizeUrl(provider: OidcProvider, redirect?: string): string {
    const base = window.Parameters?.isRuntimeServer
        ? `/rts/auth/authorize/${provider}`
        : `/ms/auth/public/preview/${
              window.Parameters?.SiteAlias ?? ''
          }/authorize/${provider}`;
    if (redirect && redirect.startsWith('/')) {
        return `${base}?da_dest=${encodeURIComponent(redirect)}`;
    }
    return base;
}

// Seam: jsdom cannot intercept location assignment, so tests spy on this.
export const navigation = {
    redirect(url: string): void {
        window.location.assign(url);
    },
};

// Popup path requires BOTH: preview (only there does /complete exist to message the opener) and a
// parent window to message. Published pages redirect, framed or not.
function isFramedPreview(): boolean {
    if (window.Parameters?.isRuntimeServer) {
        return false;
    }
    try {
        return window.self !== window.top;
    } catch {
        return true; // cross-origin parent: framed
    }
}

// Never rejects: an unreadable session reads as anonymous.
function currentUserOrNull(): Promise<AuthUser | null> {
    try {
        return me().then(
            (user) => user,
            () => null
        );
    } catch {
        return Promise.resolve(null);
    }
}

let inFlight: {
    provider: OidcProvider;
    signIn: Promise<AuthUser | null>;
} | null = null;

export function signInWithProvider({
    provider,
    redirect,
}: SignInOptions): Promise<AuthUser | null> {
    // The preview authorize path needs the alias as a path segment, so without it it can only 404.
    if (!window.Parameters?.isRuntimeServer && !window.Parameters?.SiteAlias) {
        return Promise.reject(
            new AuthError(
                0,
                'UNKNOWN',
                'sign-in cannot start: the page shell did not set window.Parameters.SiteAlias'
            )
        );
    }
    const url = authorizeUrl(provider, redirect);
    if (!isFramedPreview()) {
        navigation.redirect(url);
        return new Promise(() => {}); // page navigates away
    }
    if (inFlight) {
        // The completion message carries no provider, so only the same one can share the popup.
        if (inFlight.provider === provider) {
            return inFlight.signIn;
        }
        return Promise.reject(
            new AuthError(
                0,
                'UNKNOWN',
                `a ${inFlight.provider} sign-in is already in progress`
            )
        );
    }
    let finished = false;
    const signIn = new Promise<AuthUser | null>((resolve, reject) => {
        // Thunk so a discarded re-entrant call cannot fire an extra me() request.
        const finish = (user: () => Promise<AuthUser | null>) => {
            if (finished) {
                return; // a stale tick must not clear a newer flow's inFlight
            }
            window.removeEventListener('message', onMessage);
            clearInterval(closedPoll);
            finished = true;
            inFlight = null;
            resolve(user());
        };
        const onMessage = (event: MessageEvent) => {
            const data = event.data as {
                source?: string;
                status?: string;
                siteAlias?: string;
            } | null;
            if (
                event.origin !== window.location.origin ||
                data?.source !== PREVIEW_AUTH_MESSAGE_SOURCE
            ) {
                return;
            }
            const ownAlias = window.Parameters?.SiteAlias;
            if (ownAlias && data.siteAlias !== ownAlias) {
                return;
            }
            finish(
                data.status === 'success'
                    ? () => currentUserOrNull()
                    : () => Promise.resolve(null)
            );
        };
        let closedPoll: ReturnType<typeof setInterval> | undefined;
        window.addEventListener('message', onMessage);
        const popup = window.open(url, '_blank', 'width=520,height=720');
        if (!popup) {
            window.removeEventListener('message', onMessage);
            finished = true;
            inFlight = null;
            reject(
                new AuthError(
                    0,
                    'UNKNOWN',
                    'sign-in popup was blocked — call signInWithProvider from a direct click handler'
                )
            );
            return;
        }
        if (finished) {
            return; // resolved during window.open — no orphaned poll
        }
        // Closed without a message: the session may still be set, so read live.
        closedPoll = setInterval(() => {
            if (popup.closed) {
                finish(() => currentUserOrNull());
            }
        }, 500);
    });
    if (!finished) {
        inFlight = { provider, signIn };
    }
    return signIn;
}
